<template>
  <v-app class="app-bg">
    <!-- PREMIUM SIDEBAR -->
    <v-navigation-drawer v-if="loggedIn.authStatus" v-model="drawer" :mini-variant.sync="mini" permanent app
      color="#f8fafc" class="modern-sidebar elevation-1" width="280">

      <!-- USER PROFILE SECTION (PREMIUM) -->
      <div class="sidebar-user-section mb-2" :class="mini ? 'pa-2' : 'pa-4'">
        <v-list-item class="px-0 rounded-xl sidebar-user-card transition-swing" :to="{ name: 'UserPerfil' }">
          <v-list-item-avatar size="45" class="ma-0 avatar-shadow">
            <v-img v-if="profileImage" :src="profileImage" />
            <v-img v-else :src="require('@/assets/user.png')"></v-img>
          </v-list-item-avatar>

          <v-list-item-content v-show="!mini" class="ml-2">
            <v-list-item-title class="font-weight-black text-subtitle-2 primary--text">{{ profileName
              }}</v-list-item-title>
            <v-list-item-subtitle class="caption grey--text text--darken-1">Ver perfil</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </div>

      <v-list nav dense class="px-3">
        <!-- Home -->
        <v-list-item :to="{ path: '/' }" color="primary" class="rounded-lg mb-1 sidebar-item">
          <v-list-item-icon class="mr-4">
            <v-icon size="22">mdi-home-variant-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title class="font-weight-bold">Inicio</v-list-item-title>
        </v-list-item>

        <v-divider class="mx-4 my-4 opacity-50"></v-divider>
        <div v-if="!mini" class="px-4 mb-2 caption font-weight-black grey--text text-uppercase tracking-wider">Módulos
        </div>

        <!-- Módulos dinámicos -->
        <nav-module v-for="(module, key) in activeModules" :key="key" :icon="module.icon" :title="module.title"
          :items="module.items" :mini="mini" />

        <v-divider class="mx-4 my-4 opacity-50"></v-divider>

        <!-- Mi perfil -->
        <v-list-item link :to="{ name: 'UserPerfil' }" color="primary" class="rounded-lg sidebar-item">
          <v-list-item-icon class="mr-4">
            <v-icon size="22">mdi-account-circle-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title class="font-weight-bold">Mi cuenta</v-list-item-title>
        </v-list-item>
      </v-list>

      <!-- Logout -->
      <template v-slot:append>
        <div class="pa-4">
          <v-list nav dense class="pa-0">
            <v-list-item @click="logout" class="rounded-lg logout-item">
              <v-list-item-icon class="mr-4">
                <v-icon size="22" color="error">mdi-logout-variant</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="font-weight-bold error--text">Cerrar sesión</v-list-item-title>
            </v-list-item>
          </v-list>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- MODERN APP BAR -->
    <v-app-bar v-if="loggedIn.authStatus" flat class="modern-app-bar d-print-none" app color="primary" dark>
      <v-btn icon @click.stop="mini = !mini" class="mr-2">
        <v-icon>mdi-menu-open</v-icon>
      </v-btn>

      <Footer :environmentLabel="environmentLabel" :currentDatabase="currentDatabase" />

      <v-spacer></v-spacer>

      <v-badge
        :content="pendingApprovals"
        :value="pendingApprovals > 0"
        color="error"
        overlap
        class="mr-2"
      >
        <v-btn icon small>
          <v-icon>mdi-bell-outline</v-icon>
        </v-btn>
      </v-badge>
    </v-app-bar>

    <v-main class="main-content-bg">
      <v-container fluid class="pa-4 pa-sm-6 pa-md-8">
        <v-fade-transition mode="out-in">
          <router-view></router-view>
        </v-fade-transition>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import NavModule from "./components/NavModule.vue";
import Footer from "./components/Footer.vue";
import { menuItems } from "./config/navigation";
import { mapState, mapGetters } from "vuex";

export default {
  name: "App",

  components: {
    NavModule,
    Footer
  },

  data: () => ({
    mini: true,
    drawer: false,
    environmentLabel: process.env.NODE_ENV !== 'production' ||
      (process.env.VUE_APP_API_URL_PRODUCTION &&
        process.env.VUE_APP_API_URL_PRODUCTION.includes("3074"))
      ? "PRUEBA"
      : "PRODUCCION",
    currentDatabase: null,
    databaseCheckInterval: null
  }),

  computed: {
    ...mapState({
      loggedIn: (state) => state.auth,
      user: (state) => state.auth.user?.user,
    }),

    ...mapGetters("auth", ["globalFeatures", "pendingApprovals"]),

    activeModules() {
      return Object.entries(menuItems)
        .filter(([_, module]) => {
          return !module.feature || this.globalFeatures.includes(module.feature);
        })
        .reduce((acc, [key, module]) => {
          acc[key] = module;
          return acc;
        }, {});
    },

    profileImage() {
      return this.user?.image_storage ? this.get_storage(this.user.image_storage) : null;
    },

    profileName() {
      return this.user?.fullname?.split(" ").slice(0, 2).join(" ") || "";
    },
  },

  created() {
    // Aplicar color de tema guardado
    const savedColor = localStorage.getItem("user_primary_color");
    if (savedColor) {
      this.$vuetify.theme.themes.light.primary = savedColor;
    }

    this.getAccess();
    this.$root.$on("toggle-mini", (value) => {
      this.mini = value;
    });

    // Solo en desarrollo, consultar la base de datos cada 15 segundos
    if (process.env.NODE_ENV !== 'production') {
      this.startDatabaseCheck();
    }
  },
  beforeDestroy() {
    this.$root.$off("toggle-mini");
    if (this.databaseCheckInterval) {
      clearInterval(this.databaseCheckInterval);
    }
  },
  methods: {
    async getAccess() {
      if (this.$store.state.auth.authStatus) {
        await this.$store.dispatch("auth/GET_FEATURES");
      }
      //
    },
    logout() {
      this.$store.dispatch("auth/logOut");
      // La mutación ya no navega; la redirección se hace aquí de forma explícita.
      if (this.$route.name !== "Login") {
        this.$router.push({ name: "Login" }).catch(() => { });
      }
    },

    getStorageUrl(path) {
      // Implementa la lógica para obtener la URL del storage
      return path;
    },

    async getDatabaseInfo() {
      try {
        const response = await this.api_Get('database-info');
        if (response.status === 200) {
          const data = await response.json();
          this.currentDatabase = data.database;
        } else {
          this.currentDatabase = 'Error';
        }
      } catch (error) {
        console.error('Error fetching database info:', error);
        this.currentDatabase = 'Error';
      }
    },

    startDatabaseCheck() {
      // Consultar inmediatamente
      this.getDatabaseInfo();

      // Configurar intervalo para consultar cada 15 segundos
      this.databaseCheckInterval = setInterval(() => {
        this.getDatabaseInfo();
      }, 15000);
    },
  },
};
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

:root {
  --primary-color: var(--v-primary-base);
  --bg-main: #F4F6F9;
  --sidebar-bg: #F8FAF6;
}

.v-application {
  font-family: 'Inter', sans-serif !important;
  background-color: var(--bg-main) !important;
}

.app-bg {
  background-color: var(--bg-main) !important;
}

/* SIDEBAR STYLING */
.modern-sidebar {
  border-right: 1px solid rgba(0, 0, 0, 0.05) !important;

  .sidebar-user-section {
    position: sticky;
    top: 0;
    z-index: 2;
    background: transparent;
  }

  .sidebar-user-card {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.8);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);

    &:hover {
      background: white;
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
    }
  }

  .sidebar-item {
    margin-bottom: 4px;
    transition: all 0.2s ease;

    &.v-list-item--active {
      background: linear-gradient(135deg, var(--primary-color), var(--v-accent-base)) !important;
      color: white !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

      .v-icon {
        color: white !important;
      }
    }

    &:hover:not(.v-list-item--active) {
      background: rgba(0, 0, 0, 0.03) !important;
      color: var(--primary-color) !important;

      .v-icon {
        color: var(--primary-color) !important;
      }
    }
  }
}

/* APP BAR STYLING */
.modern-app-bar {
  z-index: 10;
}

.main-content-bg {
  background-color: var(--bg-main);
}

.logout-item {
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 82, 82, 0.05) !important;
  }
}

.avatar-shadow {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.tracking-wider {
  letter-spacing: 0.1em !important;
}

/* TRANSITIONS */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@media print {

  .no-print,
  .v-navigation-drawer,
  .v-app-bar {
    display: none !important;
  }

  .v-main {
    margin-left: 0 !important;
    width: 100% !important;
  }

  .v-container {
    max-width: 100% !important;
    padding: 0 !important;
  }

  body,
  .v-application {
    background: white !important;
    font-size: 8px !important;
  }
}
</style>
